/**
 * Keskitetty hook tilauksen statuspäivityksiin
 * 
 * Kaikki statuksen muutokset ohjataan tämän hookin kautta,
 * jolloin sähköposti-ilmoitukset lähetetään johdonmukaisesti.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { queryKeys } from "@/lib/query-keys";

interface UseOrderStatusMutationParams {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface StatusUpdateParams {
  orderId: string;
  newStatus: string;
  oldStatus?: string;
  skipDbUpdate?: boolean;
}

export type { StatusUpdateParams };

export function useOrderStatusMutation(params?: UseOrderStatusMutationParams) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, newStatus, oldStatus, skipDbUpdate }: StatusUpdateParams) => {
      let previousStatus = oldStatus;
      
      if (!skipDbUpdate) {
        if (!previousStatus) {
          const { data: order, error: fetchError } = await supabase
            .from("orders")
            .select("status")
            .eq("id", orderId)
            .single();
          
          if (fetchError) throw fetchError;
          previousStatus = order.status;
        }

        const { error: updateError } = await supabase
          .from("orders")
          .update({ 
            status: newStatus as "draft" | "confirmed" | "ready_for_installation" | "installed" | "cancelled",
            updated_at: new Date().toISOString()
          })
          .eq("id", orderId);

        if (updateError) throw updateError;
      }

      if (newStatus !== "draft" && previousStatus && previousStatus !== newStatus) {
        const { error: notifyError } = await supabase.functions.invoke("send-status-notification", {
          body: {
            order_id: orderId,
            old_status: previousStatus,
            new_status: newStatus,
          },
        });
        
        if (notifyError) {
          console.error("[useOrderStatusMutation] Sähköpostin lähetys epäonnistui:", notifyError);
        }
      }

      return { orderId, newStatus, oldStatus: previousStatus };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(result.orderId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.orders.stats() });
      queryClient.invalidateQueries({ queryKey: queryKeys.workshop.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.installations.upcoming() });
      
      params?.onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error("Virhe", { description: error.message });
      params?.onError?.(error);
    },
  });
}
